<script lang="ts" setup>
import CurrentLanguage from '@/components/CurrentLanguage.vue';
import { computed, markRaw, Ref, ref, watch } from 'vue';
import CategoryField from '../categories/CategoryField.vue';
import { Category } from '@/models/category.model';
import { EventItem } from '@/models/event-item.model';
import { Story } from '@/models/story.model';
import { LearningItem } from '@/models/learning-item.model';
import initiativesService from '@/services/initiatives.service';
import learningItemService from '@/services/learning-item.service';
import eventsService from '@/services/events.service';
import storiesService from '@/services/stories.service';
import { Initiative } from '@/models/initiative.model';
import insightService from '@/services/insight.service';
import { useStore } from 'vuex';
import { Actions, Mutations } from '@/store';
import { Location } from '@/models/location.model';
import LocationField from '../locations/LocationField.vue';
import { TYPE_INITIATIVE } from '@/util/constants';
import EventItemForm from '../events/EventItemForm.vue';
import LearningItemForm from '../learning/LearningItemForm.vue';
import ContentItemForm from './ContentItemForm.vue';
import StoryForm from '../stories/StoryForm.vue';
import { FilterData } from '@/services/users.service.js';

const headers = [
  { title: 'ID', key: 'item.id', width: '5%' },
  { title: 'Type', key: 'type', width: '10%' },
  { title: 'Title', key: 'title', width: '40%' },
  { title: 'Category', key: 'category', width: '25%' },
];

interface ResultItem {
  type: string;
  title: string;
  category: Category;
  item: Initiative | LearningItem | EventItem | Story;
}

const store = useStore();
const filteredContent = ref<ResultItem[]>([]);

const filterData = ref<FilterData>({
  search: '',
  level: null,
  page: 1,
  count: 25,
  sort: 'id',
  direction: 'asc',
});

const category = ref<Category>();
const location = ref<Location>();

const initiativesCount = ref<number>(0);
const learningItemsCount = ref<number>(0);
const eventItemsCount = ref<number>(0);
const storiesCount = ref<number>(0);

const total = computed<number>(
  () =>
    initiativesCount.value +
    learningItemsCount.value +
    eventItemsCount.value +
    storiesCount.value,
);

const search = async () => {
  if (category.value || location.value) {
    const initiatives = await initiativesService.search(
      '',
      filterData.value.page,
      category.value?.id,
      location.value?.id,
    );
    const learningItems = location.value
      ? { total: 0, items: [] }
      : await learningItemService.search(
          '',
          filterData.value.page,
          category.value?.id,
        );
    const eventItems = await eventsService.search(
      '',
      filterData.value.page,
      category.value?.id,
      location.value?.id,
    );
    const stories = location.value
      ? { total: 0, items: [] }
      : await storiesService.search(
          '',
          filterData.value.page,
          category.value?.id,
        );

    initiativesCount.value = initiatives.total;
    learningItemsCount.value = learningItems.total;
    eventItemsCount.value = eventItems.total;
    storiesCount.value = stories.total;

    filteredContent.value = [
      ...initiatives.items.map((i) => ({
        item: i,
        title: i.title,
        type: 'Initiative',
        category: i.mainCategory!,
      })),
      ...learningItems.items.map((i) => ({
        item: i,
        title: i.title,
        type: 'Learning Item',
        category: i.mainCategory!,
      })),
      ...stories.items.map((i) => ({
        item: i,
        title: i.title,
        type: 'Quiz',
        category: i.mainCategory!,
      })),
      ...eventItems.items.map((i) => ({
        item: i,
        title: i.title,
        type: 'Event',
        category: i.mainCategory!,
      })),
    ].sort((a, b) => (a.title > b.title ? 1 : -1));
  }
};

watch(() => category.value, search);
watch(() => location.value, search);

function updateCategories(isActive: Ref) {
  store.dispatch(Actions.ShowNotification, {
    message: 'Updating categories',
    type: 'info',
  });

  isActive.value = false;
  insightService.updateCategories();
}

function updateLocations() {
  store.dispatch(Actions.ShowNotification, {
    message: 'Updating locations',
    type: 'info',
  });

  insightService.updateLocations();
}

async function editStory(s: Story) {
  const story = await storiesService.get(s.id);

  store.commit(Mutations.ShowDrawer, {
    component: markRaw(StoryForm),
    data: story.data,
  });
}

function editItem(_: PointerEvent, { item }: { item: ResultItem }) {
  console.log(item);
  switch (item.type) {
    case 'Initiative':
      store.commit(Mutations.ShowDrawer, {
        component: markRaw(ContentItemForm),
        data: {
          type: TYPE_INITIATIVE,
          contentItem: item.item,
        },
      });
      break;
    case 'Event':
      store.commit(Mutations.ShowDrawer, {
        component: markRaw(EventItemForm),
        data: item.item,
      });
      break;
    case 'Learning Item':
      store.commit(Mutations.ShowDrawer, {
        component: markRaw(LearningItemForm),
        data: item.item,
      });
      break;
    case 'Quiz':
      editStory(item.item as Story);
      break;
  }
}
function updateItemsPerPage(count: number) {
  filterData.value = { ...filterData.value, count };
  search();
}
function updatePage(page: number) {
  filterData.value = { ...filterData.value, page };
  search();
}

function resetPage() {
  filterData.value = { ...filterData.value, page: 1 };
}
function updateSortBy(sorting: { key: string; order: 'asc' | 'desc' }[]) {
  if (sorting.length) {
    const { key, order } = sorting[0];

    filterData.value = { ...filterData.value, sort: key, direction: order };
    resetPage();
    search();
  }
}
</script>
<template>
  <v-container>
    <h2>Insight <CurrentLanguage /></h2>

    <v-btn
      type="button"
      color="warning"
      style="position: absolute; top: 70px; right: 260px; z-index: 1"
      @click="updateLocations"
      >Update locations</v-btn
    >
    <v-dialog width="500">
      <template v-slot:activator="{ props: activatorProps }">
        <v-btn
          type="button"
          color="warning"
          v-bind="activatorProps"
          style="position: absolute; top: 70px; right: 60px; z-index: 1"
          >Update categories</v-btn
        >
      </template>
      <template v-slot:default="{ isActive }">
        <v-card>
          <v-card-text>
            <h2>Update categories</h2>
            <p>
              The server will find main and sub for each sub-sub category, and
              re-infer main category from there. This makes searching by
              category more powerfull since also sub categories will now find
              results.
            </p>
            <v-row>
              <v-col>
                <v-btn
                  class="mt-2"
                  color="warning"
                  type="button"
                  block
                  @click="() => updateCategories(isActive)"
                  >Update categories</v-btn
                ></v-col
              >
            </v-row>
          </v-card-text>
        </v-card>
      </template>
    </v-dialog>
    <v-row>
      <v-col>
        <CategoryField label="Search by category" v-model="category" />
      </v-col>
      <v-col>
        <LocationField label="Search by location" v-model="location" />
      </v-col>
    </v-row>

    <v-chip
      density="compact"
      :color="initiativesCount === 0 ? 'warning' : 'info'"
      >{{ initiativesCount }} Initiatives</v-chip
    >&nbsp;
    <v-chip
      density="compact"
      :color="learningItemsCount === 0 ? 'warning' : 'info'"
      >{{ learningItemsCount }} Learning Items</v-chip
    >&nbsp;
    <v-chip
      density="compact"
      :color="eventItemsCount === 0 ? 'warning' : 'info'"
      >{{ eventItemsCount }} Events</v-chip
    >&nbsp;
    <v-chip density="compact" :color="storiesCount === 0 ? 'warning' : 'info'"
      >{{ storiesCount }} Quizzes</v-chip
    >&nbsp;
    <v-data-table-server
      density="compact"
      @click:row="editItem"
      :headers="headers"
      :items="filteredContent"
      :itemsLength="total"
      :itemsPerPage="filterData.count"
      :page="filterData.page"
      @update:itemsPerPage="updateItemsPerPage"
      @update:page="updatePage"
      @update:sortBy="updateSortBy"
      hover
    >
      <template v-slot:item.category="{ value }">
        {{ value.title }}
      </template>
    </v-data-table-server>
  </v-container>
</template>
